import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import Backend from "i18next-http-backend";
import { MODEL_COUNT, PROVIDER_COUNT } from "@/data/modelStats";
import { STORAGE_KEYS } from "@/config/storageKeys";

// Available languages
export const languages = [
  { code: "ar", name: "العربية", dir: "rtl", flag: "🇲🇦" },
  { code: "fr", name: "Français", dir: "ltr", flag: "🇫🇷" },
  { code: "en", name: "English", dir: "ltr", flag: "🇬🇧" },
  { code: "es", name: "Español", dir: "ltr", flag: "🇪🇸" },
  { code: "de", name: "Deutsch", dir: "ltr", flag: "🇩🇪" },
  { code: "it", name: "Italiano", dir: "ltr", flag: "🇮🇹" },
  { code: "pt", name: "Português", dir: "ltr", flag: "🇵🇹" },
  { code: "tr", name: "Türkçe", dir: "ltr", flag: "🇹🇷" },
  { code: "nl", name: "Nederlands", dir: "ltr", flag: "🇳🇱" },
  { code: "pl", name: "Polski", dir: "ltr", flag: "🇵🇱" },
  { code: "ru", name: "Русский", dir: "ltr", flag: "🇷🇺" },
  { code: "ja", name: "日本語", dir: "ltr", flag: "🇯🇵" },
  { code: "zh", name: "中文", dir: "ltr", flag: "🇨🇳" },
  { code: "ko", name: "한국어", dir: "ltr", flag: "🇰🇷" },
  { code: "hi", name: "हिन्दी", dir: "ltr", flag: "🇮🇳" },
];

// Single source of truth for the advertised language count (mirrors
// data/modelStats.js MODEL_COUNT). Derived from the languages array so the
// number can never drift from the actual supported set — consume this instead
// of hardcoding "15" in a stat/badge/copy.
export const LANGUAGE_COUNT = languages.length;

export const getLanguageDir = (code) => {
  const lang = languages.find((l) => l.code === code);
  return lang?.dir || "ltr";
};

i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // Multi-tier fallback: try English first, then French. Eight of our
    // 15 locales (tr/nl/pl/ru/ja/zh/ko/hi) are at ~50% key coverage
    // today, so when a Polish or Japanese user hits an untranslated
    // key the UI used to render in French — useless for non-Francophones.
    // English-first is the more universally readable fallback. French
    // stays in the chain so the FR-only content (blog.posts) still
    // renders for users whose UI is otherwise FR.
    fallbackLng: ["en", "fr"],
    supportedLngs: languages.map((l) => l.code),

    detection: {
      // "path" first so a per-locale URL (/en/…, /es/…) drives the language for
      // international SEO. i18next ignores a first segment that isn't in
      // supportedLngs (e.g. /pricing, /models), so the un-prefixed French root
      // and all existing routes fall through to localStorage/navigator exactly
      // as before — purely additive.
      order: ["path", "localStorage", "navigator", "htmlTag"],
      lookupFromPathIndex: 0,
      caches: ["localStorage"],
      lookupLocalStorage: STORAGE_KEYS.LANGUAGE,
    },

    backend: {
      loadPath: "/locales/{{lng}}/translation.json",
    },

    interpolation: {
      escapeValue: false,
      // Global defaults so {{MODELS}} / {{PROVIDERS}} resolve in EVERY string
      // without each t() call passing them — single source of truth = the
      // MODEL_STATS constant. Keeps the advertised catalog size honest + in one
      // place across all 15 locales (see src/data/modelStats.js).
      defaultVariables: {
        MODELS: MODEL_COUNT,
        PROVIDERS: PROVIDER_COUNT,
        // Current year for the copyright footer ({{year}}) — resolved once at
        // init so the "© {{year}}" line never goes stale and no call site
        // (6 of them: PageLayout + legal/pricing/landing) has to pass it.
        year: new Date().getFullYear(),
      },
    },

    react: {
      // Suspense ON so components don't render with raw i18n keys before the
      // translation JSON has loaded. The Suspense boundary lives in index.js
      // and renders a centered spinner while the http-backend fetches the
      // active language. Without this, first-paint flashed `landing.hero.title`
      // and similar raw keys on cold loads (Kimi audit P0, 2026-04-26).
      useSuspense: true,
    },
  });

// Update document direction when language changes
i18n.on("languageChanged", (lng) => {
  const dir = getLanguageDir(lng);
  document.documentElement.dir = dir;
  document.documentElement.lang = lng;
});

export default i18n;
